import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, CircularProgress, IconButton, Menu, MenuItem, Tab, Tabs } from '@mui/material';
import { ArrowLeft, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ChatInputScreen } from '../../../components/chat/ChatInputScreen';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { useSidebarConversations } from '../../../components/layout/useSidebarConversations';
import { useAuth } from '../../../contexts/AuthContext';
import { useNotification } from '../../../hooks/useNotification';
import { useStartNewChat } from '../../../hooks/useStartNewChat';
import { ProjectConversationRow } from '../components/ProjectConversationRow';
import { ProjectInstructionsPanel } from '../components/ProjectInstructionsPanel';
import { ProjectKnowledgePanel } from '../components/ProjectKnowledgePanel';
import { ProjectMemoryPanel } from '../components/ProjectMemoryPanel';
import { ProjectEmptyState } from '../components/ProjectEmptyState';
import { ProjectFormDialog } from '../components/ProjectFormDialog';
import { PROJECTS_ROUTE } from '../constants';
import { useMoveConversation } from '../hooks/useMoveConversation';
import { useProjects } from '../hooks/useProjects';

type ProjectTab = 'conversations' | 'instructions' | 'knowledge' | 'memory';
const PROJECT_TABS: ProjectTab[] = ['conversations', 'instructions', 'knowledge', 'memory'];

export const ProjectDetailPage: React.FC = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAnonymous } = useAuth();
  const { error, success } = useNotification();
  const { projects, isLoaded, updateProject, deleteProject } = useProjects();
  const { sessions, refreshConversations } = useSidebarConversations();
  const moveConversation = useMoveConversation();
  const { isBusy, executionMode, setExecutionMode, handleSend, handleStartRealtimeVoice } =
    useStartNewChat({ projectId });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [tab, setTab] = useState<ProjectTab>('conversations');

  const project = projects.find((item) => item.publicId === projectId);
  const projectSessions = useMemo(
    () => sessions.filter((session) => session.projectPublicId === projectId),
    [sessions, projectId]
  );

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteProject(projectId);
      if (response.success) {
        success(t('projects.notifications.deleted'));
        void refreshConversations();
        navigate(PROJECTS_ROUTE, { replace: true });
      } else {
        error(response.error || t('projects.notifications.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  if (!isLoaded) {
    return (
      <div className="flex h-full items-center justify-center bg-slate-50 py-16 dark:bg-slate-900">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="h-full bg-slate-50 p-4 dark:bg-slate-900 sm:p-6">
        <div className="mx-auto max-w-3xl">
          <ProjectEmptyState
            title={t('projects.detail.notFoundTitle')}
            description={t('projects.detail.notFoundDescription')}
            action={
              <Button component={Link} to={PROJECTS_ROUTE} variant="contained" size="small">
                {t('projects.detail.backToProjects')}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="ambient-surface h-full bg-slate-50 p-4 dark:bg-slate-900 sm:p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-6">
        <div>
          <Link
            to={PROJECTS_ROUTE}
            className="mb-3 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400"
          >
            <ArrowLeft size={14} />
            {t('projects.detail.backToProjects')}
          </Link>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="break-words text-2xl font-semibold text-gray-900 dark:text-slate-100">
                {project.name}
              </h1>
              {project.description && (
                <p className="mt-1 whitespace-pre-line break-words text-sm text-gray-600 dark:text-slate-400">
                  {project.description}
                </p>
              )}
            </div>
            <IconButton
              aria-label={t('projects.detail.options')}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              className="text-gray-500 dark:text-slate-400"
            >
              <MoreVertical size={18} />
            </IconButton>
          </div>
        </div>

        <ChatInputScreen
          compact
          showHeading={false}
          placeholder={t('projects.detail.inputPlaceholder', { name: project.name })}
          onSend={handleSend}
          isSubmitting={isBusy}
          executionMode={executionMode}
          onExecutionModeChange={setExecutionMode}
          voiceInputEnabled={!isAnonymous}
          onStartRealtimeVoice={isAnonymous ? undefined : handleStartRealtimeVoice}
          multipleAttachments={false}
        />

        <Tabs
          value={tab}
          onChange={(_, next: ProjectTab) => setTab(next)}
          variant="scrollable"
          scrollButtons="auto"
          className="border-b border-gray-200 dark:border-slate-700"
        >
          {PROJECT_TABS.map((key) => (
            <Tab
              key={key}
              value={key}
              label={t(`projects.detail.tabs.${key}`)}
              className="normal-case dark:text-slate-300"
            />
          ))}
        </Tabs>

        {tab === 'conversations' && (
          <section>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
              {t('projects.detail.conversations', { count: projectSessions.length })}
            </h2>
            {projectSessions.length === 0 ? (
              <ProjectEmptyState
                title={t('projects.detail.emptyTitle')}
                description={t('projects.detail.emptyDescription')}
              />
            ) : (
              <ul className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
                {projectSessions.map((session) => (
                  <ProjectConversationRow
                    key={session.id}
                    session={session}
                    onRemove={(sessionId) => void moveConversation(sessionId, null)}
                  />
                ))}
              </ul>
            )}
          </section>
        )}
        {tab === 'instructions' && <ProjectInstructionsPanel project={project} />}
        {tab === 'knowledge' && <ProjectKnowledgePanel projectId={project.publicId} />}
        {tab === 'memory' && <ProjectMemoryPanel projectId={project.publicId} />}
      </div>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            setIsEditOpen(true);
          }}
          className="gap-2"
        >
          <Pencil size={16} />
          {t('projects.detail.edit')}
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            setIsDeleteOpen(true);
          }}
          className="gap-2 !text-red-600 dark:!text-red-400"
        >
          <Trash2 size={16} />
          {t('projects.detail.delete')}
        </MenuItem>
      </Menu>

      <ProjectFormDialog
        open={isEditOpen}
        project={project}
        onSubmit={(values) => updateProject(project.publicId, values)}
        onClose={() => setIsEditOpen(false)}
        onSuccess={() => success(t('projects.notifications.updated'))}
      />
      <ConfirmDialog
        open={isDeleteOpen}
        title={t('projects.detail.deleteTitle')}
        message={t('projects.detail.deleteMessage', { name: project.name })}
        confirmText={t('projects.detail.delete')}
        cancelText={t('common.cancel')}
        confirmColor="error"
        loading={isDeleting}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
};
